/**
 * @fileoverview Promotional Cover Screen Component for Memorama (Memory 1 vs 1) on stadium giant screens (/screen).
 * Renders high-impact promotional branding with sponsor/club logos, neon breathing title animations,
 * dynamic theme backgrounds (color or image), and contrast-safe typography.
 * Implements a Zero-Canvas architecture with GPU Layer Compositing (0% CPU usage in idle state).
 * Executes proactive image caching (preloadImages) before match start.
 * 
 * @module components/Screen/Sections/Memory/MemoryCover
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetConfig, GetTheme, preloadThemeOnce } from '../../../../utilities/BL/Memory';

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_COVER_CONFIGURATION = {
    coverTitle: 'MEMORAMA',
    coverBackgroundType: 'color',
    coverBackgroundColor: '#0a0a14',
    coverBackgroundUrl: '',
    coverTextColor: '#ffffff',
    brandLogoUrl: '',
    sponsorLogoUrl: ''
};

/**
 * Fallback SVG para logotipo genérico en caso de error de carga.
 * @constant {string}
 */
const DEFAULT_FALLBACK_LOGO_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="%231e293b" stroke="%233b82f6" stroke-width="4"/><text x="50" y="58" font-family="Arial,sans-serif" font-size="28" font-weight="bold" fill="%23ffffff" text-anchor="middle">★</text></svg>`;

/**
 * MemoryCover Component - Portada Promocional de Memorama para Pantalla Gigante
 * 
 * @function MemoryCover
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - WebSocket payload or initial data from parent Screen router.
 * @param {Object} [props.theme] - Theme object directly passed from Screen.
 * @param {Object} [props.config] - Config object directly passed from Screen.
 * @param {string} [props.roomId] - Room GUID.
 * @returns {React.ReactElement} The rendered stadium cover view.
 */
export const MemoryCover = ({ payload, theme: propTheme, config: propConfig, roomId }) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA VISUAL ACTIVO
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);

    /**
     * Resuelve rutas de archivos multimedia a URLs absolutas completas.
     * 
     * @function resolveMediaUrl
     * @param {string|null} mediaPath - Ubicación relativa o absoluta del recurso.
     * @returns {string} URL absoluta del recurso.
     */
    const resolveMediaUrl = useCallback((mediaPath) => {
        if (!mediaPath) return '';
        if (
            mediaPath.startsWith('http://') ||
            mediaPath.startsWith('https://') ||
            mediaPath.startsWith('blob:') ||
            mediaPath.startsWith('data:')
        ) {
            return mediaPath;
        }
        let apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/').replace('/api/', '/').replace('/api', '/');
        
        if (typeof window !== 'undefined' && window.location && window.location.hostname && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
            if (apiBaseEndpointUrl.includes('localhost') || apiBaseEndpointUrl.includes('127.0.0.1')) {
                apiBaseEndpointUrl = apiBaseEndpointUrl.replace('localhost', window.location.hostname).replace('127.0.0.1', window.location.hostname);
            }
            if (window.location.protocol === 'http:' && apiBaseEndpointUrl.startsWith('https://')) {
                apiBaseEndpointUrl = apiBaseEndpointUrl.replace('https://', 'http://');
            }
        }

        const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
        return `${apiBaseEndpointUrl}${sanitizedMediaPath}`;
    }, []);

    /**
     * Consulta el tema activo de la sala desde el backend si no vino por props.
     * 
     * @async
     * @function fetchActiveThemeConfiguration
     */
    const fetchActiveThemeConfiguration = useCallback(async () => {
        if (!roomId) return;
        try {
            const configurationResponse = await GetConfig(roomId);
            if (configurationResponse.success && configurationResponse.data) {
                const roomConfiguration = configurationResponse.data;
                if (roomConfiguration.activeTheme) {
                    setActiveThemeData(roomConfiguration.activeTheme);
                    preloadThemeOnce(roomConfiguration.activeTheme);
                } else if (roomConfiguration.activeThemeId) {
                    const themeResponse = await GetTheme(roomConfiguration.activeThemeId, roomId);
                    if (themeResponse.success && themeResponse.data) {
                        setActiveThemeData(themeResponse.data);
                        preloadThemeOnce(themeResponse.data);
                    }
                }
            }
        } catch (themeFetchError) {
            // Fallback no bloqueante
        }
    }, [roomId]);

    // Sincronización continua de tema por props o payload de websocket
    useEffect(() => {
        if (propTheme) {
            const directTheme = propTheme.theme && typeof propTheme.theme === 'object' ? propTheme.theme : propTheme;
            setActiveThemeData(directTheme);
            preloadThemeOnce(directTheme);
            return;
        }

        if (propConfig) {
            const confTheme = propConfig.theme || propConfig.activeTheme;
            if (confTheme) {
                setActiveThemeData(confTheme);
                preloadThemeOnce(confTheme);
                return;
            }
        }

        if (payload) {
            const incomingThemeData = payload.state?.currentTheme ||
                payload.data?.currentTheme ||
                payload.payload?.currentTheme ||
                payload.state?.theme ||
                payload.data?.theme ||
                payload.payload?.theme ||
                payload.theme;

            if (incomingThemeData) {
                setActiveThemeData(incomingThemeData);
                preloadThemeOnce(incomingThemeData);
                return;
            }
        }

        if (!activeThemeData) {
            fetchActiveThemeConfiguration();
        }
    }, [payload, propTheme, propConfig, activeThemeData, fetchActiveThemeConfiguration]);

    // =========================================================================
    // 2. EXTRACCIÓN DE ATRIBUTOS (Soporte camelCase y PascalCase) Y FALLBACKS
    // =========================================================================
    const coverBackgroundType = activeThemeData?.coverBackgroundType || activeThemeData?.CoverBackgroundType || DEFAULT_COVER_CONFIGURATION.coverBackgroundType;
    const coverBackgroundColor = activeThemeData?.coverBackgroundColor || activeThemeData?.CoverBackgroundColor || DEFAULT_COVER_CONFIGURATION.coverBackgroundColor;
    const rawCoverBgUrl = activeThemeData?.coverBackgroundUrl || activeThemeData?.CoverBackgroundUrl;
    const coverBackgroundUrl = rawCoverBgUrl ? resolveMediaUrl(rawCoverBgUrl) : '';
    const coverTextColor = activeThemeData?.coverTextColor || activeThemeData?.CoverTextColor || DEFAULT_COVER_CONFIGURATION.coverTextColor;
    const coverTitleText = activeThemeData?.coverTitle || activeThemeData?.CoverTitle || DEFAULT_COVER_CONFIGURATION.coverTitle;
    const rawBrandLogo = activeThemeData?.brandLogoUrl || activeThemeData?.BrandLogoUrl;
    const brandLogoUrl = rawBrandLogo ? resolveMediaUrl(rawBrandLogo) : '';
    const rawSponsorLogo = activeThemeData?.sponsorLogoUrl || activeThemeData?.SponsorLogoUrl;
    const sponsorLogoUrl = rawSponsorLogo ? resolveMediaUrl(rawSponsorLogo) : '';

    // =========================================================================
    // 3. RENDERIZADO DEL FONDO DINÁMICO (ZERO-CANVAS GPU COMPOSITED)
    // =========================================================================
    const renderDynamicBackgroundLayer = () => {
        if (coverBackgroundType === 'image' && coverBackgroundUrl) {
            return (
                <div
                    className="memory-screen-cover__bg-image"
                    style={{ backgroundImage: `url(${coverBackgroundUrl})` }}
                />
            );
        }

        return (
            <div
                className="memory-screen-cover__bg-color"
                style={{ backgroundColor: coverBackgroundColor }}
            />
        );
    };

    // =========================================================================
    // 4. RENDERIZADO PRINCIPAL DE LA PORTADA
    // =========================================================================
    return (
        <main
            className="memory-screen-cover"
            role="region"
            aria-label="Portada Promocional Memorama 1 vs 1"
        >
            {/* Capa de Fondo Dinámico */}
            {renderDynamicBackgroundLayer()}

            {/* Capa de Viñeta y Filtro de Contraste GPU */}
            <div className="memory-screen-cover__vignette-overlay" />

            {/* Partículas y Luces de Fondo Neón */}
            <div className="memory-screen-cover__ambient-glow" />

            {/* Contenido Central Flotante */}
            <div className="memory-screen-cover__content-wrapper">
                {/* Contenedor de Logotipos (Club / Patrocinador) */}
                <div className="memory-screen-cover__logos-container">
                    {sponsorLogoUrl && (
                        <div className="memory-screen-cover__logo-box memory-screen-cover__logo-box--sponsor">
                            <img
                                src={sponsorLogoUrl}
                                alt="Patrocinador Oficial"
                                className="memory-screen-cover__sponsor-logo"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = DEFAULT_FALLBACK_LOGO_SVG;
                                }}
                            />
                        </div>
                    )}

                    <div className="memory-screen-cover__logo-box memory-screen-cover__logo-box--brand">
                        <img
                            src={brandLogoUrl || '/img/page/adsl-five.png'}
                            alt="Marca / Club"
                            className="memory-screen-cover__brand-logo"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/img/page/adsl-five.png';
                            }}
                        />
                    </div>
                </div>

                {/* Título Principal de la Dinámica con Animación Flotante y Respiración Neón */}
                <h1
                    className="memory-screen-cover__hero-title"
                    style={{ color: coverTextColor }}
                >
                    {coverTitleText}
                </h1>
            </div>

            {/* Pie de Página Corporativo con Créditos */}
            <footer
                className="memory-screen-cover__footer-credits"
                style={{ color: '#ffffff', fontSize: '20px' }}
            >
                Hecho con ♥ por Interaktia
            </footer>
        </main>
    );
};

MemoryCover.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object
    }),
    theme: PropTypes.object,
    config: PropTypes.object,
    roomId: PropTypes.string
};

MemoryCover.defaultProps = {
    payload: null,
    theme: null,
    config: null,
    roomId: null
};

export default MemoryCover;
